import type { RestructuredProduct } from '../infrastructure/SourcingClient';
import type { GlowpickProduct } from './Glowpick';
import type { CMWorkflowStatus } from './CatalogManagement';

export enum ScrapingStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface CMStatusHistoryEntry {
  toStatus: string;
  fromStatus?: string;
  operator: string;
  updatedAt: string;
  reason?: string;
  message?: string;
}

// CpGpMatched uses the unified CoupangProduct interface
export interface CpGpMatchedProduct {
  cpProductId: string; // pk
  cpItemId: string; // sk
  gpProductId: string; // GSI index-gpProductId : pk
  keyword: string; // search keyword
  cpDetailedProduct: CoupangProduct;
  gpDetailedProduct?: GlowpickProduct; // Full Glowpick product details
  ssRestructuredProducts?: RestructuredProduct[]; // SS product structure after restructuring from CP
  scrapingStatus?: ScrapingStatus; // track background scraping progress
  workflowStatus?: CMWorkflowStatus; // CM workflow status
  cmStatus?: string; // CM status
  cmStatusHistory?: CMStatusHistoryEntry[]; // CM status history
  modifiedAt?: string;
  month?: string;
  gpi?: string;
  wave?: string;
  cppi?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GpKeywordCpSearch {
  gpProductId: string; // pk
  cpUniqueId: string; // sk
  cpProductId: string; // GSI index-cpProductId-cpItemId : pk
  cpItemId: string; // GSI index-cpProductId-cpItemId : sk
  keyword: string; // GSI index-keyword : pk (gpBrand + gpName)
  cpSearchedProduct: CoupangProduct; // keyword search result product
  createdAt?: string;
  updatedAt?: string;
}

export type CPSearchList = GpKeywordCpSearch[];

export interface ProductOption {
  itemId: string;
  itemName: string;
  vendorItemId: string;
  finalPrice: string;
  unitPrice: string; // "(10g당 15,917원)"
  delivery: ShippingInfo;
  stock: boolean;
  imageUrl?: string;
  hoverImageUrl?: string;
}

export enum ShippingType {
  ROCKET = 'rocket',
  SELLER_ROCKET = 'seller rocket',
  SELLER_DELIVERY = 'seller delivery',
}

export interface ShippingInfo {
  shippingType: ShippingType;
  shippingFee?: number;
  deliveryDays?: number; // how long it takes to deliver
  deliveryDescription?: string;
}

export interface PriceInfo {
  salePrice: string;
  originalPrice?: string;
  discountRate?: string;
  unitPrice?: string;
}

export interface SellerInfo {
  sellerName: string;
  sellerRating?: string;
  sellerReviewCount?: number;
}

export interface CoupangProduct {
  // Core identifiers
  productId?: string;
  itemId?: string;
  vendorItemId?: string;

  // Basic product info
  name: string;
  brand: string;

  priceInfo?: PriceInfo;
  sellerInfo?: SellerInfo;

  // Reviews and ratings
  rating?: number;
  reviews?: number;
  ranking?: number;

  // Images - that are stored in S3
  thumbnailImageUrl?: string;
  heroImageUrls?: string[];
  detailedImageUrls?: string[];
  verifiedImageUrls?: string[]; // Verified/translated image URLs by AI Agent or human

  // Images - coupang image cdn images
  _thumbnailImageUrl?: string;
  _heroImageUrls?: string[];
  _detailedImageUrls?: string[];

  // Shipping
  shipping?: string; // legacy field
  shippingInfo?: ShippingInfo; // new field

  // URL
  productPageUrl?: string;

  options?: ProductOption[];
  outOfStock?: boolean;
}

export interface CoupangFilters {
  search: string;
  brand: string;
  category: string;
  subcategory: string;
}

export interface CoupangSearchParams {
  filters: CoupangFilters;
  sortBy: 'name' | 'price' | 'rating' | 'reviewCount';
  sortOrder: 'asc' | 'desc';
  limit: number;
}

export interface CoupangSearchResponse {
  products: CoupangProduct[];
  total?: number;
}
